/**
 * Centralized address field configuration
 * Single source of truth for all address field metadata
 */

export const ADDRESS_FIELDS = {
  fullName: {
    propertyName: "fullName",
    formFieldName: "full_name",
    selector: ".js-address-editor-full-name",
    errorSelector: ".js-address-editor-full-name-error",
    hasValidator: true,
    required: false,
    dataType: "string"
  },
  address1: {
    propertyName: "address1",
    formFieldName: "address1",
    selector: ".js-address-editor-address-line-1",
    errorSelector: ".js-address-editor-address-line-1-error",
    hasValidator: true,
    required: true,
    dataType: "string"
  },
  address2: {
    propertyName: "address2",
    formFieldName: "address2",
    selector: ".js-address-editor-address-line-2",
    errorSelector: ".js-address-editor-address-line-2-error",
    hasValidator: false,
    required: false,
    dataType: "string"
  },
  zipcode: {
    propertyName: "zipcode",
    formFieldName: "zipcode",
    selector: ".js-address-editor-zip-code",
    errorSelector: ".js-address-editor-zip-code-error",
    hasValidator: true,
    required: true,
    dataType: "string"
  },
  city: {
    propertyName: "city",
    formFieldName: "city",
    selector: ".js-address-editor-city",
    errorSelector: ".js-address-editor-city-error",
    hasValidator: true,
    required: true,
    dataType: "string"
  },
  phone: {
    propertyName: "phone",
    formFieldName: "phone",
    selector: ".js-address-editor-phone",
    errorSelector: ".js-address-editor-phone-error",
    hasValidator: true,
    required: false,
    dataType: "string"
  },
  stateId: {
    propertyName: "stateId",
    formFieldName: "state_id",
    selector: null,
    errorSelector: null,
    hasValidator: false,
    required: false,
    dataType: "number"
  },
  stateName: {
    propertyName: "stateName",
    formFieldName: "state_name",
    selector: null,
    errorSelector: null,
    hasValidator: false,
    required: false,
    dataType: "string"
  },
  customAddress: {
    propertyName: "customAddress",
    formFieldName: "custom_address",
    selector: null,
    errorSelector: null,
    hasValidator: false,
    required: false,
    dataType: "boolean"
  }
};

/**
 * Get array of all field property names
 * @returns {string[]} ['fullName', 'address1', ...]
 */
export const getFieldNames = () => Object.keys(ADDRESS_FIELDS);

/**
 * Get array of form field names (snake_case)
 * @returns {string[]} ['full_name', 'address1', ...]
 */
export const getFormFieldNames = () =>
  Object.values(ADDRESS_FIELDS).map(field => field.formFieldName);

/**
 * Get array of fields that have validators
 * @returns {string[]} ['fullName', 'address1', 'zipcode', 'city', 'phone']
 */
export const getValidatableFields = () =>
  Object.keys(ADDRESS_FIELDS).filter(key => ADDRESS_FIELDS[key].hasValidator);

/**
 * Get mapping of CSS selector to field property name
 * @returns {Object} {'.js-address-editor-full-name': 'fullName', ...}
 */
export const getSelectorMapping = () =>
  Object.entries(ADDRESS_FIELDS)
    .filter(([, config]) => config.selector !== null)
    .reduce(
      (acc, [_, config]) => ({
        ...acc,
        [config.selector]: config.propertyName
      }),
      {}
    );

/**
 * Get mapping of field property name to CSS selector
 * @returns {Object} {'fullName': '.js-address-editor-full-name', ...}
 */
export const getFieldToSelectorMap = () =>
  Object.entries(ADDRESS_FIELDS)
    .filter(([, config]) => config.selector !== null)
    .reduce(
      (acc, [_, config]) => ({
        ...acc,
        [config.propertyName]: config.selector
      }),
      {}
    );

/**
 * Get mapping of form field name (snake_case) to property name (camelCase)
 * @returns {Object} {'full_name': 'fullName', 'address1': 'address1', ...}
 */
export const getFormFieldMapping = () =>
  Object.values(ADDRESS_FIELDS).reduce(
    (acc, config) => ({
      ...acc,
      [config.formFieldName]: config.propertyName
    }),
    {}
  );

/**
 * Get mapping of field property name to error container selector
 * @returns {Object} {'fullName': '.js-address-editor-full-name-error', ...}
 */
export const getErrorSelectors = () =>
  Object.entries(ADDRESS_FIELDS)
    .filter(([, config]) => config.errorSelector !== null)
    .reduce(
      (acc, [_, config]) => ({
        ...acc,
        [config.propertyName]: config.errorSelector
      }),
      {}
    );
